import React from 'react';

import { Text } from '@rbilabs/universal-components';
import { centsToDollarString } from 'utils';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import Currency from 'components/currency';
import Dialog from 'components/dialog';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';

import { ModalBodyContainer, ModalItemContainer, ModalSubtitleContainer } from './styled';
import { IDeliveryFeesTier } from './types';

export const DeliveryFeesStripeModal: React.FC<React.PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  fees: IDeliveryFeesTier;
  smallCartFeeThreshold: number;
}>> = ({
  open,
  onClose,
  fees: { fee, serviceFeePercent, smallCartFee, geographicalFee },
  smallCartFeeThreshold,
}) => {
  const { formatCurrencyForLocale } = useUIContext();

  if (!open) {
    return null;
  }
  return (
    <Dialog
      showDialog={open}
      testID="delivery-fees-stripe-modal"
      heading={t('deliveryFees')}
      bodyComponent={
        <ModalBodyContainer>
          <ModalItemContainer testID="delivery-fee-item">
            <ModalSubtitleContainer>
              {t('deliveryFee')}
              {': '}
              <Currency amount={fee} />
            </ModalSubtitleContainer>
            {t('deliveryFeeDescription')}
          </ModalItemContainer>
          {geographicalFee > 0 && (
            <ModalItemContainer testID="geographical-fee-item">
              <ModalSubtitleContainer>
                {t('geographicalFee')}
                {': '}
                <Currency amount={geographicalFee} />
              </ModalSubtitleContainer>
              {t('geographicalFeeDescription', {
                geographicalFeeAmount: centsToDollarString(geographicalFee),
              })}
            </ModalItemContainer>
          )}
          {serviceFeePercent > 0 && (
            <ModalItemContainer testID="service-fee-item">
              <ModalSubtitleContainer>
                {t('serviceFee')}
                {': '}
                <Text>{serviceFeePercent}%</Text>
              </ModalSubtitleContainer>
              {t('serviceFeeDescription', {
                percent: serviceFeePercent,
              })}
            </ModalItemContainer>
          )}
          {smallCartFee > 0 && (
            <ModalItemContainer testID="small-cart-fee-item">
              <ModalSubtitleContainer>
                {t('smallCartFee')}
                {': '}
                <Currency amount={smallCartFee} />
              </ModalSubtitleContainer>
              {smallCartFee > 0 &&
                t('smallCartFeeDescription', {
                  amount: formatCurrencyForLocale(smallCartFeeThreshold),
                })}
            </ModalItemContainer>
          )}
        </ModalBodyContainer>
      }
      actions={
        <ActionButton
          onPress={onClose}
          testID="delivery-fees-modal-ok-btn"
          variant={ActionButtonVariants.TEXT_ONLY}
        >
          {t('okayButtonText')}
        </ActionButton>
      }
      modalAppearanceEventMessage="Delivery Fees Stripe Modal"
      onDismiss={onClose}
    />
  );
};
